import { type ReactNode } from 'react'
import { cn } from '../../lib/utils'

type PageHeaderSize = 'sm' | 'md' | 'lg' | 'xl'
type PageHeaderVariant = 'default' | 'bordered' | 'gradient' | 'accent'

interface PageHeaderProps {
	title: ReactNode
	subtitle?: ReactNode
	size?: PageHeaderSize
	variant?: PageHeaderVariant
	actions?: ReactNode
	className?: string
	children?: ReactNode
}

const sizeStyles: Record<PageHeaderSize, { title: string; subtitle: string; container: string }> = {
	sm: {
		title: 'text-lg font-medium',
		subtitle: 'text-sm',
		container: 'py-3',
	},
	md: {
		title: 'text-xl font-semibold',
		subtitle: 'text-sm',
		container: 'py-4',
	},
	lg: {
		title: 'text-2xl font-bold',
		subtitle: 'text-base',
		container: 'py-6',
	},
	xl: {
		title: 'text-3xl font-bold tracking-tight',
		subtitle: 'text-sm',
		container: 'py-6',
	},
}

const variantStyles: Record<PageHeaderVariant, string> = {
	default: '',
	bordered: 'border-b border-gray-200 dark:border-gray-700',
	gradient: '',
	accent: '',
}

export function PageHeader({
	title,
	subtitle,
	size = 'md',
	variant = 'default',
	actions,
	className,
	children,
}: PageHeaderProps) {
	const styles = sizeStyles[size]
	const titleCls =
		variant === 'gradient'
			? cn(styles.title, 'font-extrabold bg-clip-text text-transparent brand-gradient')
			: cn(styles.title, 'text-gray-900 dark:text-gray-100')

	return (
		<div
			className={cn(
				'flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4',
				styles.container,
				variantStyles[variant],
				className,
			)}
		>
			<div className="flex-1 min-w-0 space-y-1">
				<h1 className={titleCls}>{title}</h1>
				{variant === 'accent' && (
					<span aria-hidden className="block h-0.5 w-20 rounded brand-gradient" />
				)}
				{subtitle && (
					<p className={cn('text-gray-500 dark:text-gray-400', styles.subtitle)}>
						{subtitle}
					</p>
				)}
				{children}
			</div>
			{actions && (
				<div className="flex flex-shrink-0 items-center gap-2">{actions}</div>
			)}
		</div>
	)
}

export default PageHeader
